// frontend/src/theme/palette.ts
// -----------------------------------------------------------------------------
// Athena Tech Brand Color Palette
// -----------------------------------------------------------------------------
// This file contains all shared color tokens used across theme variants.
// Colors are based on the Athena Tech brand identity (athenatechai.com)
// -----------------------------------------------------------------------------

/**
 * Athena Tech Brand Colors
 * These are the core colors that define the brand identity
 */
export const athenaColors = {
  // 🌑 Primary Brand Colors (Deep Blues)
  deepNavy: "#092047",      // Primary dark - headers, navbar, backgrounds
  royalBlue: "#0b468c",     // Primary - buttons, links, interactive elements
  
  // 🎨 Secondary Brand Colors (Light Blues)
  periwinkle: "#b7c1de",    // Secondary - glass overlays, soft UI elements
  lightPeriwinkle: "#dbe0ee", // Lightest blue for subtle backgrounds
  blueGray: "#5a6a8a",      // Text secondary, muted elements
  
  // 💜 Accent Colors (Purple Family)
  softPurple: "#ac61b9",    // Primary accent - selections, active states
  brightPurple: "#8c00a5",  // Brand purple - gradients only (very low opacity)
  lightPurple: "#c98fd4",   // Hover states for purple elements
  
  // 🔶 Action Colors (Orange Family)
  orange: "#FF8C00",        // CTAs, important actions, highlights
  lightOrange: "#FFB347",   // Hover states, softer orange
  
  // ⬜ Neutrals
  white: "#FFFFFF",
  black: "#000000",
};

/**
 * Semantic Colors
 * Used for status indicators, alerts, and feedback
 */
export const semanticColors = {
  // Success (Healthcare-appropriate green)
  success: {
    main: "#00875A",
    light: "#36B37E",
    dark: "#006644",
    contrastText: "#FFFFFF",
  },
  
  // Warning (Brand orange, softer)
  warning: {
    main: "#FFB347",
    light: "#FFD280",
    dark: "#FF8C00",
    contrastText: "#000000",
  },
  
  // Error (Clear red for critical)
  error: {
    main: "#DE350B",
    light: "#FF5630",
    dark: "#BF2600",
    contrastText: "#FFFFFF",
  },
  
  // Info (Brand blue)
  info: {
    main: "#0b468c",
    light: "#b7c1de",
    dark: "#092047",
    contrastText: "#FFFFFF",
  },
};

/**
 * Glass Effect Tokens
 * Standardized values for glassmorphism effects
 */
export const glassTokens = {
  // Blur values
  blurLight: "blur(8px)",
  blurMedium: "blur(12px)",
  blurHeavy: "blur(16px)",
  
  // Opacity ranges for glass backgrounds
  glassOpacity: {
    subtle: 0.08,
    light: 0.12,
    medium: 0.18,
    strong: 0.25,
  },
  
  // Border opacity for glass edges
  borderOpacity: {
    subtle: 0.15,
    light: 0.2,
    medium: 0.3,
    strong: 0.4,
  },
  
  // Container opacity for layout boxes (less transparent = higher values)
  containerOpacity: {
    light: 0.6,    // Subtle panels, secondary areas
    medium: 0.75,  // Main glass containers
    heavy: 0.88,   // Drawers, modals, overlays
  },
};

/**
 * Content Text Presets
 * Text colors for content displayed on container surfaces
 * Choose based on container background brightness
 */
export const contentTextPresets = {
  // For LIGHT backgrounds (athena_gradient theme)
  // Use dark text for contrast on pastel gradient
  onLight: {
    primary: athenaColors.deepNavy,      // #092047 — Main text, high contrast
    secondary: athenaColors.royalBlue,   // #0b468c — Secondary text, branded blue
    muted: athenaColors.blueGray,        // #5a6a8a — Muted/disabled text
  },
  
  // For DARK backgrounds (classic theme)
  // Use branded light text for contrast on deep navy
  onDark: {
    primary: athenaColors.lightPeriwinkle, // #dbe0ee — Main text, soft blue-white
    secondary: athenaColors.periwinkle,    // #b7c1de — Secondary text
    muted: athenaColors.blueGray,          // #5a6a8a — Muted/disabled text
  },
};

/**
 * Shadow Tokens
 * Consistent shadows for depth and elevation
 */
export const shadowTokens = {
  // Soft shadows for glass elements
  glassShadow: (color: string, opacity = 0.2) => 
    `0 8px 32px rgba(${hexToRgb(color)}, ${opacity})`,
  
  // Elevation shadows
  elevation1: "0 2px 8px rgba(9, 32, 71, 0.08)",
  elevation2: "0 4px 16px rgba(9, 32, 71, 0.12)",
  elevation3: "0 8px 32px rgba(9, 32, 71, 0.18)",
  elevation4: "0 16px 48px rgba(9, 32, 71, 0.24)",
  
  // Neumorphic shadows
  neumorphicOuter: "8px 8px 16px rgba(9, 32, 71, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.05)",
  neumorphicInner: "inset 4px 4px 8px rgba(9, 32, 71, 0.15), inset -4px -4px 8px rgba(255, 255, 255, 0.05)",
};

/**
 * Helper: Convert hex to RGB values
 */
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "0, 0, 0";
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
}

/**
 * Typography
 * Font families and weights
 */
export const typography = {
  fontFamily: '"Inter", "Inter var", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
  fontFamilyBrand: '"MontserratAthena", "Montserrat", sans-serif',
  fontFamilyMono: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
  
  weights: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
};

const paletteExports = {
  athenaColors,
  semanticColors,
  glassTokens,
  shadowTokens,
  typography,
  contentTextPresets,
};

export default paletteExports;
